import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import Header from '../../components/ui/UnifiedHeader';
import SharedFooter from '../../components/SharedFooter';
import { EnhancedBreadcrumb } from '../../components/ui/breadcrumb.jsx';
import { usePageMeta } from '../../components/BreadcrumbProvider.jsx';
import { useAnchorScroll } from '../../hooks/useAnchorScroll';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StickySubNav from '../../components/ui/StickySubNav';
import CompanyOverview from './components/CompanyOverview';
import VisionMission from './components/VisionMission';
// import EnhancedManagementTeam from './components/EnhancedManagementTeam'; // Removed temporarily
// import AdvisoryBoard from './components/AdvisoryBoard'; // Removed - no content
import CompanyTimeline from './components/CompanyTimeline';

const AboutUsCompanyFoundationLeadership = () => {
  // Handle anchor scrolling
  useAnchorScroll();
  
  const breadcrumbs = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about-us-company-foundation-leadership' }
  ];

  const sections = [
    { id: 'overview', name: 'Overview', icon: 'BookOpen' },
    { id: 'vision-mission', name: 'Vision & Mission', icon: 'Target' },
    { id: 'timeline', name: 'Timeline', icon: 'Calendar' },
    { id: 'success-stories', name: 'Success Stories', icon: 'Award' }
  ];

  // Set page metadata with breadcrumbs
  usePageMeta({
    title: 'About Us - Company Foundation & Leadership',
    breadcrumbs: breadcrumbs
  });

  const aboutPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Bootminds - Company Foundation & Leadership",
    "description": "Bootminds was founded in 2017 in Bangalore, partnered with Intelliswift from 2018-2024, and relaunched as an independent service-first GCC partner in January 2025 after Intelliswift's acquisition by LTTS.",
    "mainEntity": {
      "@type": "Organization",
      "name": "Bootminds Elite",
      "legalName": "BootMinds Systems Pvt Ltd.",
      "description": "Independent India GCC setup, expansion, executive hiring, and SkillStack staffing partner.",
      "foundingDate": "2017",
      "numberOfEmployees": "50-100",
      "foundingLocation": {
        "@type": "Place",
        "name": "Bangalore, India"
      },
      "founder": [
        {
          "@type": "Person",
          "name": "Sanjay Jain",
          "jobTitle": "Founder & CEO",
          "sameAs": "https://www.linkedin.com/in/saanjayjain/"
        }
      ],
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "IN",
        "addressRegion": "Karnataka",
        "addressLocality": "Bengaluru"
      }
    }
  };

  // LocalBusiness schemas for the two real offices — gives AI engines explicit geographic anchors
  const bangaloreOfficeSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://bootminds.com/#bengaluru-office",
    "name": "Bootminds Elite — Bengaluru office",
    "parentOrganization": {
      "@type": "Organization",
      "name": "Bootminds Elite",
      "legalName": "BootMinds Systems Pvt Ltd."
    },
    "url": "https://bootminds.com/contact",
    "description": "India headquarters of Bootminds Elite — GCC setup, expansion, executive hiring, and SkillStack staffing.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "101-101, F2 Mahagony, Manyata Tech Park, Nagwara",
      "addressLocality": "Bengaluru",
      "addressRegion": "Karnataka",
      "postalCode": "560045",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 13.0461,
      "longitude": 77.6213
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    "areaServed": [
      { "@type": "Country", "name": "India" },
      { "@type": "Place", "name": "Asia-Pacific" }
    ],
    "priceRange": "$$$"
  };

  const camarilloOfficeSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://bootminds.com/#camarillo-office",
    "name": "Bootminds Elite — Camarillo office",
    "parentOrganization": {
      "@type": "Organization",
      "name": "Bootminds Elite",
      "legalName": "BootMinds Systems Pvt Ltd."
    },
    "url": "https://bootminds.com/contact",
    "description": "US office of Bootminds Elite — engagement and account management for North American clients.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1000 Paseo Camarillo, Suite 215",
      "addressLocality": "Camarillo",
      "addressRegion": "CA",
      "postalCode": "93010",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 34.2164,
      "longitude": -119.0376
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "17:00"
    },
    "areaServed": [
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "Canada" }
    ],
    "priceRange": "$$$"
  };

  const schemaData = [aboutPageSchema, bangaloreOfficeSchema, camarilloOfficeSchema];

  return (
    <>
      <SEOHead
        title="About Us - Company Foundation & Leadership | Bootminds"
        description="Learn about Bootminds's company foundation, leadership team, and mission to transform global ambition into Indian excellence for enterprise clients."
        keywords="Bootminds leadership, company foundation, executive team, advisory board, GCC consulting experts, Fortune 500 advisors"
        canonicalUrl="/about"
        breadcrumbs={breadcrumbs}
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Breadcrumb Navigation */}
        <div className="bg-background border-b border-border">
          <div className="container-executive py-4">
            <EnhancedBreadcrumb items={breadcrumbs} />
          </div>
        </div>
        
        <main className="pt-16 lg:pt-20">
          {/* In-Page Navigation */}
          <StickySubNav sections={sections} />

          <CompanyOverview />
          <VisionMission />
          {/* Leadership Team removed temporarily */}
          {/* Advisory Board removed - no content */}
          <CompanyTimeline />
          {/* Success Stories Section */}
          <section id="success-stories" className="py-20 bg-muted/30">
            <div className="container-executive">
              <div className="text-center mb-12">
                <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-body-medium mb-4">
                  <Icon name="Award" size={16} />
                  <span>Proven Results</span>
                </div>
                <h2 className="font-headline font-headline-bold text-3xl lg:text-4xl text-text-primary mb-4">
                  Client Success Stories
                </h2>
                <p className="text-text-secondary max-w-2xl mx-auto mb-8">
                  See how we've helped enterprises establish and scale their Global Capability Centers in India
                </p>
                <Link to="/case-studies">
                  <Button variant="primary">
                    <Icon name="ArrowRight" size={18} className="mr-2" />
                    View All Success Stories
                  </Button>
                </Link>
              </div>
            </div>
          </section>
          
          {/* Contact Section */}
          <section className="py-20 bg-gradient-to-br from-primary/5 to-accent/5">
            <div className="container-executive">
              <div className="text-center max-w-4xl mx-auto">
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-body-medium mb-6">
                  <Icon name="Mail" size={14} />
                  <span>Get in Touch</span>
                </div>
                
                <h2 className="font-headline font-headline-bold text-3xl lg:text-4xl text-text-primary mb-6">
                  Ready to Transform Your
                  <span className="block text-accent font-accent">Global Operations?</span>
                </h2>
                
                <p className="text-body-lg text-text-secondary max-w-2xl mx-auto mb-8">
                  Connect with our team to discuss how Bootminds can help establish your Global Capability Center with proven expertise and guaranteed results.
                </p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button 
                    variant="default" 
                    size="lg"
                    iconName="Mail"
                    iconPosition="left"
                    asChild
                  >
                    <Link to="/contact">Contact Our Team</Link>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg"
                    iconName="ArrowRight"
                    iconPosition="right"
                    asChild
                  >
                    <Link to="/services">Explore Our Services</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>
        <SharedFooter />
      </div>
    </>
  );
};

export default AboutUsCompanyFoundationLeadership;